import { useState, useEffect, useRef } from 'react';
import { Download, Share2, RefreshCw, ArrowLeft, FileText, Globe, FileDown } from 'lucide-react';

export default function GuideResult({ guide, onBack, onRegenerate }) {
  const [loading, setLoading] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadRef = useRef(null);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setDownloadOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRegenerate = async () => {
    setLoading(true);
    await onRegenerate();
    setLoading(false);
  };

  // TXT 다운로드
  const handleDownloadTxt = () => {
    const element = document.createElement('a');
    const file = new Blob([guide], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `비즈니스_가이드_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // HTML 다운로드
  const handleDownloadHtml = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI 비즈니스 가이드</title>
    <style>
        body {
            font-family: 'Malgun Gothic', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            background-color: #f8fafc;
            color: #1a202c;
        }
        .header {
            text-align: center;
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #3b82f6;
            margin: 0;
            font-size: 2.5em;
        }
        .header .subtitle {
            color: #6b7280;
            margin-top: 10px;
            font-size: 1.1em;
        }
        .content {
            background: white;
            padding: 40px;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            white-space: pre-wrap;
            line-height: 1.8;
        }
        .footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            color: #6b7280;
            font-size: 0.9em;
        }
        .generation-date {
            text-align: right;
            color: #6b7280;
            font-size: 0.9em;
            margin-bottom: 20px;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>📋 AI 비즈니스 가이드</h1>
        <div class="subtitle">✅ 5분 내 완성 | ⚡ 90% 실행 가능 | 🎯 맞춤형 전략</div>
    </div>

    <div class="generation-date">
        생성일: ${new Date().toLocaleDateString('ko-KR')}
    </div>

    <div class="content">${guide}</div>

    <div class="footer">
        <p>💡 이 가이드는 AI가 생성한 것으로, 실제 상황에 맞게 조정하여 사용하세요.</p>
        <p>Powered by AI Business Guide Maker</p>
        <p>원작: 「경영컨설팅은 끝났다」</p>
    </div>
</body>
</html>`;

    const element = document.createElement('a');
    const file = new Blob([htmlContent], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    element.download = `비즈니스_가이드_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // PDF 다운로드 (인쇄 기능 활용)
  const handleDownloadPdf = () => {
    // 새 창에서 인쇄용 페이지 열기
    const printWindow = window.open('', '_blank');
    const htmlContent = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI 비즈니스 가이드</title>
    <style>
        @media print {
            @page {
                size: A4;
                margin: 2cm;
            }
            body {
                font-size: 12pt;
                line-height: 1.5;
            }
        }
        body {
            font-family: 'Malgun Gothic', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #1a202c;
        }
        .header {
            text-align: center;
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #3b82f6;
            margin: 0;
            font-size: 2.2em;
        }
        .header .subtitle {
            color: #6b7280;
            margin-top: 10px;
            font-size: 1em;
        }
        .content {
            white-space: pre-wrap;
            line-height: 1.7;
            page-break-inside: avoid;
        }
        .footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            color: #6b7280;
            font-size: 0.9em;
            page-break-inside: avoid;
        }
        .generation-date {
            text-align: right;
            color: #6b7280;
            font-size: 0.9em;
            margin-bottom: 20px;
        }
        .no-print {
            display: none;
        }
        @media screen {
            .no-print {
                display: block;
                text-align: center;
                margin: 20px 0;
            }
            .print-btn {
                background: #3b82f6;
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 8px;
                cursor: pointer;
                font-size: 16px;
                margin: 0 10px;
            }
            .print-btn:hover {
                background: #2563eb;
            }
        }
    </style>
</head>
<body>
    <div class="no-print">
        <button class="print-btn" onclick="window.print()">📄 PDF로 저장</button>
        <button class="print-btn" onclick="window.close()">❌ 닫기</button>
        <p style="color: #6b7280; font-size: 14px; margin-top: 10px;">
            '📄 PDF로 저장' 버튼을 클릭하고 인쇄 대화상자에서 'PDF로 저장'을 선택하세요.
        </p>
    </div>

    <div class="header">
        <h1>📋 AI 비즈니스 가이드</h1>
        <div class="subtitle">✅ 5분 내 완성 | ⚡ 90% 실행 가능 | 🎯 맞춤형 전략</div>
    </div>

    <div class="generation-date">
        생성일: ${new Date().toLocaleDateString('ko-KR')}
    </div>

    <div class="content">${guide}</div>

    <div class="footer">
        <p>💡 이 가이드는 AI가 생성한 것으로, 실제 상황에 맞게 조정하여 사용하세요.</p>
        <p>Powered by AI Business Guide Maker</p>
        <p>원작: 「경영컨설팅은 끝났다」</p>
    </div>
</body>
</html>`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'AI 비즈니스 가이드',
          text: guide.slice(0, 200) + '...',
          url: window.location.href
        });
      } catch (err) {
        console.log('공유 취소됨');
      }
    } else {
      navigator.clipboard.writeText(guide);
      alert('가이드가 클립보드에 복사되었습니다!');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="btn btn-secondary flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          새로운 가이드 만들기
        </button>

        <div className="flex space-x-2">
          <button
            onClick={handleRegenerate}
            disabled={loading}
            className="btn btn-secondary flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            다시 생성
          </button>

          {/* 다운로드 드롭다운 */}
          <div className="relative" ref={downloadRef}>
            <button
              onClick={() => setDownloadOpen(!downloadOpen)}
              className="btn btn-secondary flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              다운로드
            </button>

            {downloadOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="py-1">
                  <button
                    onClick={() => {
                      handleDownloadTxt();
                      setDownloadOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    TXT 파일
                  </button>
                  <button
                    onClick={() => {
                      handleDownloadHtml();
                      setDownloadOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    HTML 파일
                  </button>
                  <button
                    onClick={() => {
                      handleDownloadPdf();
                      setDownloadOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF 파일
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleShare}
            className="btn btn-primary flex items-center"
          >
            <Share2 className="h-4 w-4 mr-2" />
            공유하기
          </button>
        </div>
      </div>

      {/* 가이드 내용 */}
      <div className="card p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">📋 AI 비즈니스 가이드</h2>
            <div className="text-sm text-gray-500">
              생성일: {new Date().toLocaleDateString('ko-KR')}
            </div>
          </div>
          <p className="text-gray-600 mt-2">
            ✅ 5분 내 완성 | ⚡ 90% 실행 가능 | 🎯 맞춤형 전략
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          <div
            className="whitespace-pre-wrap leading-relaxed"
            style={{ fontFamily: 'inherit' }}
          >
            {guide}
          </div>
        </div>

        {/* 책 구매 링크 섹션 */}
        <div className="border-t pt-6 mt-8">
          <div className="flex items-center justify-center mb-4">
            <a
              href="https://search.shopping.naver.com/book/catalog/56947386015?cat_id=50005827&frm=PBOKMOD&query=%EA%B2%BD%EC%98%81%EC%BB%A8%EC%84%A4%ED%8C%85%EC%9D%80+%EB%81%9D%EB%82%AC%EB%8B%A4&NaPm=ct%3Dmg1m0sds%7Cci%3D8c87413a4bfd29d68d2d76afc282a9ce7a4306cc%7Ctr%3Dboknx%7Csn%3D95694%7Chk%3D1fa8c0e795bf3f5edb2f20fe467a07e57715e09b"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
            >
              <div className="relative">
                <img
                  src="/book-cover.jpg"
                  alt="경영컨설팅은 끝났다 책 표지"
                  className="w-12 h-16 object-cover rounded shadow-sm group-hover:scale-105 transition-transform duration-200"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* 이미지 로드 실패시 대체 표시 */}
                <div className="w-12 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded shadow-sm flex items-center justify-center text-white text-xs font-bold"
                     style={{display: 'none'}}>
                  <div className="text-center leading-tight">
                    <div>경영</div>
                    <div>컨설팅은</div>
                    <div>끝났다</div>
                  </div>
                </div>
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-gray-800 group-hover:text-blue-600">
                  📚 원작 도서 구매하기
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  「경영컨설팅은 끝났다」 - 네이버 쇼핑
                </div>
                <div className="text-xs text-blue-600 mt-1 group-hover:underline">
                  더 깊이있는 비즈니스 인사이트 ↗
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* 푸터 */}
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div>
              💡 이 가이드는 AI가 생성한 것으로, 실제 상황에 맞게 조정하여 사용하세요.
            </div>
            <div>
              Powered by AI Business Guide Maker
            </div>
          </div>
        </div>
      </div>

      {/* 추가 액션 */}
      <div className="mt-6 text-center">
        <p className="text-gray-600 mb-4">
          가이드가 도움이 되셨나요? 다른 사업 아이디어도 분석해보세요!
        </p>
        <button
          onClick={onBack}
          className="btn btn-primary"
        >
          새로운 비즈니스 분석하기
        </button>
      </div>
    </div>
  );
}